const includesSearch = (values, query) => {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  return values.some((value) => String(value || '').toLowerCase().includes(q));
};

const isValidRow = (row) => row && typeof row === 'object';

export const buildSalaryOverviewParams = (startDate, endDate, asOfDate, extra = {}) => ({
  params: {
    start_date: startDate,
    end_date: endDate,
    as_of_date: asOfDate,
    ...extra,
  },
});

export const getSalaryCycleMonthReference = (startDate, endDate) => {
  const startMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(startDate || ''));
  const endMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(endDate || ''));
  if (!startMatch || !endMatch) return '';
  if (startMatch[1] !== endMatch[1] || startMatch[2] !== endMatch[2] || startMatch[3] !== '01') {
    return '';
  }
  const year = Number(startMatch[1]);
  const month = Number(startMatch[2]);
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return Number(endMatch[3]) === lastDay ? `${startMatch[1]}-${startMatch[2]}` : '';
};

export const filterSalarySummaryRows = (rows = [], { staffFilter = '', search = '' } = {}) => {
  return (rows || []).filter((row) => {
    if (!isValidRow(row)) return false;
    if (staffFilter && String(row.staffId) !== String(staffFilter)) return false;
    return includesSearch([
      row.staffName,
      row.department,
      row.totalAdvance,
      row.totalDeductions,
      row.totalBonus,
      row.totalGift,
      row.signedCarryForward,
      row.payableSalaryBeforeCarry,
      row.overpaymentCarriedForward,
      row.unpaidAsOfToday,
      row.totalPayableOnDueDate,
      row.currentDueDate,
    ], search);
  });
};

export const buildSalaryEventStaffIds = (rows = []) => {
  const ids = new Set();
  (rows || []).filter(isValidRow).forEach((row) => {
    const id = row.staffId || row.staff || row.staff_id;
    if (id) ids.add(String(id));
  });
  return ids;
};

export const alignSalarySummaryRowsWithEvents = (summaryRows = [], eventRows = []) => {
  const dueRowsByStaff = {};
  (eventRows || []).filter(isValidRow).forEach((row) => {
    if (row.rowType !== 'due') return;
    const id = row.staffId || row.staff || row.staff_id;
    if (!id) return;
    const key = String(id);
    if (!dueRowsByStaff[key]) dueRowsByStaff[key] = [];
    dueRowsByStaff[key].push(row);
  });
  Object.values(dueRowsByStaff).forEach((rows) => {
    rows.sort((a, b) => String(a.date || '').localeCompare(String(b.date || '')));
  });

  return (summaryRows || [])
    .filter(isValidRow)
    .map((row) => {
      const staffId = String(row.staffId || row.staff || row.staff_id);
      const dueRows = dueRowsByStaff[staffId] || [];
      const matchingDue = dueRows.find((dueRow) => String(dueRow.date || '') === String(row.currentDueDate || ''));
      const rangeDue = matchingDue || dueRows[0];
      return {
        ...row,
        dueInSelectedRangeDate: rangeDue?.date || '',
        dueInSelectedRangeAmount: parseFloat(rangeDue?.amount || 0),
      };
    });
};

export const filterSalaryEventRows = (rows = [], { staffFilter = '', search = '', entryFilter = 'all' } = {}) => {
  return (rows || []).filter((row) => {
    if (!isValidRow(row)) return false;
    if (staffFilter && String(row.staffId) !== String(staffFilter)) return false;
    if (entryFilter !== 'all' && String(row.rowType || row.type) !== String(entryFilter)) return false;
    return includesSearch([
      row.staffName,
      row.department,
      row.note,
      row.status,
      row.rowType,
      row.type,
      row.amount,
      row.remainingDue,
    ], search);
  });
};

export const buildSalaryGrandTotal = (rows = []) => {
  const safeRows = (rows || []).filter(isValidRow);
  return {
    count: safeRows.length,
    totalOverpaymentCarriedForward: safeRows.reduce((sum, row) => sum + parseFloat(row.overpaymentCarriedForward || 0), 0),
    totalSignedCarryForward: safeRows.reduce((sum, row) => sum + parseFloat(row.signedCarryForward || 0), 0),
    totalPayableSalaryBeforeCarry: safeRows.reduce((sum, row) => sum + parseFloat(row.payableSalaryBeforeCarry || 0), 0),
    totalUnpaidAsOfToday: safeRows.reduce((sum, row) => sum + parseFloat(row.unpaidAsOfToday || 0), 0),
    totalPayableOnDueDate: safeRows.reduce((sum, row) => sum + parseFloat(row.totalPayableOnDueDate || 0), 0),
    totalAdvance: safeRows.reduce((sum, row) => sum + parseFloat(row.totalAdvance || 0), 0),
    totalDeductions: safeRows.reduce((sum, row) => sum + parseFloat(row.totalDeductions || 0), 0),
    totalBonus: safeRows.reduce((sum, row) => sum + parseFloat(row.totalBonus || 0), 0),
    totalGift: safeRows.reduce((sum, row) => sum + parseFloat(row.totalGift || 0), 0),
  };
};

export const buildSalaryCalendarData = (rows = []) => {
  const data = {};
  (rows || []).forEach((row) => {
    if (!isValidRow(row)) return;
    const ds = row.date;
    if (!ds) return;
    if (!data[ds]) {
      data[ds] = {
        paidEntries: [],
        paidTotal: 0,
        advanceEntries: [],
        advanceTotal: 0,
        bonusEntries: [],
        bonusTotal: 0,
        giftEntries: [],
        giftTotal: 0,
        deductionEntries: [],
        deductionTotal: 0,
        dueEntries: [],
        dueTotal: 0,
      };
    }
    const entry = {
      id: row.id,
      staffId: row.staffId,
      staffName: row.staffName,
      amount: parseFloat(row.amount || 0),
      amountNumber: parseFloat(row.amount || 0),
      remainingDue: parseFloat(row.remainingDue || 0),
      remainingSigned: parseFloat(row.remainingSigned != null ? row.remainingSigned : (row.remainingDue || 0)),
      dueTrace: row.dueTrace || null,
      date: ds,
      rowType: row.rowType,
    };
    if (row.rowType === 'salary') {
      data[ds].paidEntries.push(entry);
      data[ds].paidTotal += entry.amount;
    } else if (row.rowType === 'advance') {
      data[ds].advanceEntries.push(entry);
      data[ds].advanceTotal += entry.amount;
    } else if (row.rowType === 'bonus') {
      data[ds].bonusEntries.push(entry);
      data[ds].bonusTotal += entry.amount;
    } else if (row.rowType === 'gift') {
      data[ds].giftEntries.push(entry);
      data[ds].giftTotal += entry.amount;
    } else if (row.rowType === 'deduction') {
      data[ds].deductionEntries.push(entry);
      data[ds].deductionTotal += entry.amount;
    } else if (row.rowType === 'due') {
      data[ds].dueEntries.push(entry);
      data[ds].dueTotal += entry.amount;
    }
  });
  return data;
};

export const formatSignedCarry = (value, formatCurrency) => {
  const amount = parseFloat(value || 0);
  if (!Number.isFinite(amount) || amount === 0) return formatCurrency(0);
  return `${amount > 0 ? '+' : '−'}${formatCurrency(Math.abs(amount))}`;
};

export const formatSalaryEventDue = (row = {}, formatAmount = (value) => String(value)) => {
  const signed = Number(row.remainingSigned ?? row.remainingDue ?? 0);
  if (Number.isFinite(signed) && signed < 0) {
    return `Credit ${formatAmount(Math.abs(signed))}`;
  }
  return `? Due ${formatAmount(Number(row.remainingDue || 0))}`;
};

export const buildSalaryMonthDueTotals = (rows = []) => {
  return (rows || []).reduce((acc, row) => {
    if (!isValidRow(row)) return acc;
    if (row.rowType !== 'due' || !row.date) return acc;
    const monthKey = String(row.date).slice(0, 7);
    if (!acc[monthKey]) {
      acc[monthKey] = { count: 0, totalNet: 0 };
    }
    acc[monthKey].count += 1;
    acc[monthKey].totalNet += parseFloat(row.amount || 0);
    return acc;
  }, {});
};

export const buildSalaryStaffOptions = (...sources) => {
  const optionMap = new Map();
  const rows = sources.length > 1 ? sources.flat() : (sources[0] || []);
  (rows || []).filter(isValidRow).forEach((row) => {
    const id = row.staffId || row.staff || row.staff_id;
    if (!id) return;
    const key = String(id);
    const name = row.staffName || row.name || 'Unknown Staff';
    const terminated = row.employmentStatus === 'terminated';
    const existing = optionMap.get(key);
    if (!existing) {
      optionMap.set(key, { name, terminated });
    } else if (terminated && !existing.terminated) {
      existing.terminated = true;
    }
  });
  return Array.from(optionMap.entries())
    .map(([value, info]) => ({
      value,
      label: `${info.name}${info.terminated ? ' (Terminated)' : ''}`,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

export const buildSalaryPaymentStaffOptions = (activeOptions = [], summaryRows = [], keepValue = null) => {
  const options = new Map(
    (activeOptions || []).map((option) => [String(option.value), option])
  );
  buildSalaryStaffOptions(
    (summaryRows || []).filter((row) => (
      Math.abs(Number(row.runningSalaryBalance ?? row.unpaidAsOfToday ?? 0)) > 0.01
      || (keepValue != null && String(row.staffId) === String(keepValue))
    ))
  ).forEach((option) => options.set(String(option.value), option));

  return Array.from(options.values()).sort((a, b) => a.label.localeCompare(b.label));
};

export const buildSalaryCycleRangeLabel = (row, formatDate) => {
  if (!row || typeof formatDate !== 'function') return '-';
  const start = row.previousCycleStart;
  const end = row.previousCycleEnd || row.previousCycleDueDate;
  if (start && end) return `${formatDate(start)} to ${formatDate(end)}`;
  if (end) return formatDate(end);
  return '-';
};
